import { tokenRefreshManager } from "./tokenRefreshManager";
import {
  getAccessTokenFromStorage,
  getRefreshTokenFromStorage,
} from "./tokenStorage";
import { sanitizeErrorForLog } from "./sanitizeErrorForLog";

export type UserRole = "Admin" | "Staff" | "Doctor" | "Viewer";
export type JwtPayload = {
  sub: string;
  unique_name: string;

  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name": string;
  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier": string;

  jti: string;
  ver: string; // "164" comes as string, so keeping string
  iat: number;
  nbf: number;
  exp: number;

  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role": string;

  roles: UserRole;

  // Set by Api/Services/AuthenticationService.cs — the account the token is scoped to.
  account_id: string;

  iss: string;
  aud: string;
};

export interface UserIdentity {
  // Stable, non-PII identifier — the backend user id (ClaimTypes.NameIdentifier),
  // falling back to account_id, and only as a last resort to the email in `sub`.
  userId: string;
  accountId: string | null;
  // True only when we had to fall back to the email claim — callers should treat
  // this as a loud "this distinct_id is PII" signal.
  isPii: boolean;
}

export function getUserIdentity(token: string): UserIdentity | null {
  const payload = getAccessTokenPayload(token);
  if (!payload) return null;

  // Kept function-local rather than module-level: this module sits in an import
  // cycle (tokenFunctions → tokenRefreshManager → useAuthStore → tokenFunctions),
  // so a module-level const can still be in its temporal dead zone when a caller
  // re-enters through the cycle.
  const nameIdentifierClaim =
    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier";

  const accountId = payload.account_id ?? null;
  const nameIdentifier = payload[nameIdentifierClaim];

  if (typeof nameIdentifier === "string" && nameIdentifier.trim() !== "") {
    return { userId: nameIdentifier, accountId, isPii: false };
  }

  if (typeof accountId === "string" && accountId.trim() !== "") {
    return { userId: accountId, accountId, isPii: false };
  }

  // Last resort: `sub` is the user's email in this backend (see
  // AuthenticationService.cs). This is PII — only reached if the user id and
  // account id claims are both absent from the token.
  if (typeof payload.sub === "string" && payload.sub.trim() !== "") {
    return { userId: payload.sub, accountId, isPii: true };
  }

  return null;
}

function base64UrlDecode(str: string) {
  // convert from base64url to base64
  const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  try {
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function getAccessTokenPayload(token: string) {
  const parts = token.split(".");
  if (parts.length < 2) {
    // Not a JWT, just return it
    return null;
  }

  const payload = base64UrlDecode(parts[1]);
  return payload as JwtPayload;
}

export function getTokenExpirationDate(token: string): Date | null {
  if (!token) return null;

  const payload = getAccessTokenPayload(token);
  if (!payload || typeof payload.exp !== "number") return null;

  // exp in JWT is in seconds → convert to milliseconds
  return new Date(payload.exp * 1000);
}
export function isTokenExpired(token: string): boolean {
  const date = getTokenExpirationDate(token);
  if (!date) return true; // treat missing/invalid token as expired

  return date.getTime() <= Date.now();
}

export function getAccessRoles(token: string): UserRole[] {
  if (!token) return [];

  const payload = getAccessTokenPayload(token);
  if (!payload) return [];

  // Common role claim sources
  const directRoles = payload.roles; // might be string or array
  const claimRole =
    payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

  const roles: string[] = [];

  // 1. If payload.roles exists
  if (Array.isArray(directRoles)) {
    roles.push(...directRoles);
  } else if (typeof directRoles === "string") {
    roles.push(
      ...directRoles
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean)
    );
  }

  // 2. Microsoft role claim
  if (Array.isArray(claimRole)) {
    roles.push(...claimRole);
  } else if (typeof claimRole === "string") {
    roles.push(claimRole);
  }

  // Remove duplicates + keep only valid UserRole literals
  const validRoles: UserRole[] = ["Admin", "Staff", "Doctor", "Viewer"];

  return Array.from(new Set(roles)).filter((r): r is UserRole =>
    validRoles.includes(r as UserRole)
  );
}

export async function getAccessToken(): Promise<string | null> {
  const token = getAccessTokenFromStorage();
  const refreshToken = getRefreshTokenFromStorage();

  // If no tokens at all, return null immediately
  if (!token || !refreshToken) {
    console.warn("[getAccessToken] No tokens in storage");
    return null;
  }

  try {
    const payload = getAccessTokenPayload(token);
    const exp = payload?.exp;
    const now = Math.floor(Date.now() / 1000);
    const buffer = 30; // seconds before expiry to proactively refresh

    // Token is still valid with buffer
    if (typeof exp === "number" && exp > now + buffer) {
      return token;
    }

    // Token is expired or about to expire - refresh it
    const newToken = await tokenRefreshManager.refreshAccessToken("proactive refresh (< 30s to expiry)");
    return newToken;
  } catch (err) {
    // If we get here, the token refresh manager already handled logout and redirect
    console.error("[getAccessToken] Token refresh failed", sanitizeErrorForLog(err));
    return null;
  }
}
